import { assertNever } from "assert-never";
import * as monaco from "monaco-editor-core";

import { Panic } from "../Errors/Panic";
import {
  CursorPos,
  KeyModifiers,
  KeyPress,
  KeyPressCommand,
  makeKeyPress,
  SpecialKey,
  TerminalSize,
  TermSurface,
  TermSurfaceChange
} from "../gen/Terminal";
import styles from "./Terminal.module.css";

export class Terminal implements monaco.IDisposable {
  static measureSize(root: HTMLElement): TerminalSize {
    return measureSizeForTerminal(root);
  }

  constructor(parent: HTMLElement, termSurface: TermSurface, events: Terminal.Events) {
    this.root = document.createElement("div");
    this.root.className = styles.root;

    this.terminalCont = document.createElement("div");
    this.terminalCont.className = styles.terminal_cont;

    this.root.appendChild(this.terminalCont);

    parent.appendChild(this.root);

    const resizeObserver = new ResizeObserver((entries) => {
      setImmediate(() => {
        this.editorInstance.layout();

        const tmpCont = document.createElement("div");
        tmpCont.className = styles.terminal_cont;
        this.root.appendChild(tmpCont);
        const size = measureSizeForTerminal(tmpCont);
        this.root.removeChild(tmpCont);
        events.onResize(size);
      });
    });

    resizeObserver.observe(parent);

    this.editorInstance = createMonacoEditor(this.terminalCont);

    const model = this.editorInstance.getModel();
    if (model === null) {
      throw new Panic("monaco editor no model", {});
    }
    this.model = model;

    const contents = termSurface.screen.map((l) => l.text).join("\n");

    this.model.setValue(contents);

    this.cursorPos = termSurface.cursorPos;
    this.updateCursorPosition();

    this.editorInstance.onKeyDown((e) => {
      const keyPressCommand = keyEventToKeypressCommand(e.browserEvent);
      if (keyPressCommand !== null) {
        e.preventDefault();
        e.stopPropagation();
        events.onKeyPressCommand(keyPressCommand);
      }
    });

    this.editorInstance.onDidChangeCursorPosition((e) => {
      if (e.reason === monaco.editor.CursorChangeReason.Explicit) {
        this.updateCursorPosition();
      }
    });

    this.editorInstance.focus();
  }

  dispose(): void {
    throw new Error("Method not implemented.");
  }

  private updateCursorPosition(): void {
    this.editorInstance.setPosition({
      lineNumber: this.cursorPos.line + 1,
      column: this.cursorPos.col + 1
    });
  }

  processTermSurfaceChange(termSurfaceChange: TermSurfaceChange): void {
    this.queuedTermSurfaceChanges.push(termSurfaceChange);
    setImmediate(() => this.processTermSurfaceChangesQueue());
  }

  private processTermSurfaceChangesQueue(): void {
    for (const termSurfaceChange of this.queuedTermSurfaceChanges) {
      switch (termSurfaceChange.kind) {
        case "updateLine":
          this.model.applyEdits([
            {
              range: {
                startLineNumber: termSurfaceChange.value.line + 1,
                startColumn: 1,
                endLineNumber: termSurfaceChange.value.line + 1,
                endColumn: termSurfaceChange.value.termLine.text.length + 1
              },
              text: termSurfaceChange.value.termLine.text
            }
          ]);

          this.updateCursorPosition();
          break;
        case "appendScrollBack":
          break;
        case "clearScrollBack":
          break;
        case "setScrollBackVisible":
          break;
        case "clearScrollBackStart":
          break;
        case "clearScrollBackEnd":
          break;
        case "moveCursor":
          this.cursorPos = termSurfaceChange.value.cursorPos;
          this.updateCursorPosition();
          break;
        case "resize":
          console.log("resize", termSurfaceChange.value.size);
          this.model.setValue("\n".repeat(Math.max(0, termSurfaceChange.value.size.height - 1)));
          // if (termSurfaceChange.value.size.height < this.model.getLineCount()) {
          //   this.model.applyEdits([{
          //     range: {
          //       startLineNumber: termSurfaceChange.value.size.height,
          //       startColumn: 0,
          //       endLineNumber: this.model.getLineCount(),
          //       endColumn:
          //     },
          //     text: null
          //   }]);
          // }
          break;
        case "setWindowTitle":
          break;
        default:
          assertNever(termSurfaceChange);
      }
    }
    this.queuedTermSurfaceChanges = [];
  }

  // hide(): void {
  //   this.editorInstance.setModel(null);
  // }

  private readonly root: HTMLElement;
  private readonly terminalCont: HTMLElement;
  private cursorPos: CursorPos;
  private readonly editorInstance: monaco.editor.IStandaloneCodeEditor;
  private readonly model: monaco.editor.ITextModel;
  private queuedTermSurfaceChanges: TermSurfaceChange[] = [];
}

export declare namespace Terminal {
  export interface Events {
    onKeyPressCommand: (keyPressCommand: KeyPressCommand) => void;
    onResize: (size: TerminalSize) => void;
  }
}

function measureSizeForTerminal(root: HTMLElement): TerminalSize {
  root.style.background = "pink";
  const domRect = root.getBoundingClientRect();
  const editor = createMonacoEditor(root);
  const model = editor.getModel();
  if (model === null) {
    throw new Panic("monaco editor no model", {});
  }
  const verticalScrollbarWidth = editor.getLayoutInfo().verticalScrollbarWidth;
  model.setValue("\n".repeat(256));
  const target = editor.getTargetAtClientPoint(
    domRect.x + domRect.width - 0.5 - verticalScrollbarWidth,
    domRect.y + domRect.height - 0.5
  );
  if (target === null) {
    model.dispose();
    editor.dispose();
    return {
      width: 0,
      height: 0
    };
  }
  if (target.position === null) {
    model.dispose();
    editor.dispose();
    return {
      width: target.mouseColumn - 1,
      height: 0
    };
  }
  model.dispose();
  editor.dispose();
  return {
    width: target.mouseColumn - 1,
    height: target.position.lineNumber - 1
  };
}

function createMonacoEditor(parent: HTMLElement): monaco.editor.IStandaloneCodeEditor {
  // "Is there a way to disable IntelliSense?" <https://github.com/microsoft/monaco-editor/issues/1681>
  const editorInstance = monaco.editor.create(parent, {
    matchBrackets: "never",
    selectionHighlight: false,
    tabSize: 2,
    theme: "vs",
    readOnly: true,
    rulers: [],
    lineNumbers: "off",
    padding: {
      top: 0,
      bottom: 0
    },
    glyphMargin: false,
    renderIndentGuides: false,
    codeLens: false,
    lightbulb: {
      enabled: false
    },
    folding: false,
    lineDecorationsWidth: 0,
    scrollBeyondLastLine: false,
    revealHorizontalRightPadding: 0,
    scrollPredominantAxis: true,
    scrollbar: {
      verticalHasArrows: true,
      useShadows: false,
      arrowSize: 15,
      horizontal: "hidden",
      verticalScrollbarSize: 15
    },
    cursorStyle: "block",
    detectIndentation: false,
    autoClosingBrackets: "never",
    quickSuggestions: false,
    snippetSuggestions: "none",
    occurrencesHighlight: false,
    contextmenu: false,
    autoIndent: "none",
    suggest: {
      showStatusBar: false,
      showSnippets: false
    },
    dragAndDrop: false,
    suggestOnTriggerCharacters: false,
    minimap: {
      enabled: false
    }
  });

  // Disable horizontal scrolling
  editorInstance.onDidScrollChange((e) => {
    if (e.scrollLeftChanged) {
      editorInstance.setScrollLeft(0, monaco.editor.ScrollType.Immediate);
    }
  });

  return editorInstance;
}

function keyEventToKeypressCommand(e: KeyboardEvent): KeyPressCommand | null {
  const keyPress = keyboardEventToKeyPress(e);
  if (keyPress === null) {
    return null;
  } else {
    return {
      keyPress: keyPress,
      keyModifiers: keyboardEventModifiers(e)
    };
  }
}

function keyboardEventToKeyPress(e: KeyboardEvent): KeyPress | null {
  switch (e.code) {
    case "Escape":
      return makeKeyPress("specialKey", SpecialKey.escape);
    case "F1":
      return makeKeyPress("specialKey", SpecialKey.f1);
    case "F2":
      return makeKeyPress("specialKey", SpecialKey.f2);
    case "F3":
      return makeKeyPress("specialKey", SpecialKey.f3);
    case "F4":
      return makeKeyPress("specialKey", SpecialKey.f4);
    case "F5":
      return makeKeyPress("specialKey", SpecialKey.f5);
    case "F6":
      return makeKeyPress("specialKey", SpecialKey.f6);
    case "F7":
      return makeKeyPress("specialKey", SpecialKey.f7);
    case "F8":
      return makeKeyPress("specialKey", SpecialKey.f8);
    case "F9":
      return makeKeyPress("specialKey", SpecialKey.f9);
    case "F10":
      return makeKeyPress("specialKey", SpecialKey.f10);
    case "F11":
      return makeKeyPress("specialKey", SpecialKey.f11);
    case "F12":
      return makeKeyPress("specialKey", SpecialKey.f12);
    case "Insert":
    case "Numpad0":
      return makeKeyPress("specialKey", SpecialKey.insert);
    case "Delete":
    case "NumpadDecimal":
      return makeKeyPress("specialKey", SpecialKey.delete);
    case "Home":
    case "Numpad7":
      return makeKeyPress("specialKey", SpecialKey.home);
    case "End":
    case "Numpad1":
      return makeKeyPress("specialKey", SpecialKey.end);
    case "PageUp":
    case "Numpad9":
      return makeKeyPress("specialKey", SpecialKey.pageUp);
    case "PageDown":
    case "Numpad3":
      return makeKeyPress("specialKey", SpecialKey.pageDown);
    case "Tab":
      return makeKeyPress("specialKey", SpecialKey.tab);
    case "Enter":
    case "NumpadEnter":
      return makeKeyPress("specialKey", SpecialKey.enter);
    case "Backspace":
      return makeKeyPress("specialKey", SpecialKey.backspace);
    case "ArrowLeft":
    case "Numpad4":
      return makeKeyPress("specialKey", SpecialKey.arrowLeft);
    case "ArrowRight":
    case "Numpad6":
      return makeKeyPress("specialKey", SpecialKey.arrowRight);
    case "ArrowUp":
    case "Numpad8":
      return makeKeyPress("specialKey", SpecialKey.arrowUp);
    case "ArrowDown":
    case "Numpad2":
      return makeKeyPress("specialKey", SpecialKey.arrowDown);
    default:
      break;
  }
  if (keyboardEventIsPrintable(e)) {
    return makeKeyPress("char", e.key);
  } else {
    // Ignore any other keys (like "Shift", or "CapsLock")
    return null;
  }
}

function keyboardEventIsPrintable(e: KeyboardEvent): boolean {
  return e.key.length === 1 || /[^a-zA-Z0-9]/.test(e.key);
}

function keyboardEventModifiers(e: KeyboardEvent): KeyModifiers {
  return {
    shift: e.shiftKey,
    ctrl: e.ctrlKey,
    alt: e.altKey,
    capsLock: e.getModifierState("CapsLock")
  };
}
